import React, { useState, useContext } from 'react';
import parse from 'html-react-parser';
import SelectAccounts from '../select-accounts';
import ShippingPreferencesTabPane from './shipping-preferences-tab-pane';
import { authContext } from '../../../authorization/auth-context';
import { Buckets } from '../../../../models/buckets-enum';
import {
    WrappedComponentProps,
    injectIntl,
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { useShippingPreferences } from '../../../../store/preferences/shipping-preferences-hooks';
import { AccountType } from '../../../../models/account';
import { Formik } from 'formik';
import FormActions from '../../../common/form-actions';
import PageTitle from '../../../common/page.title';
import ConfirmBilToModal from './confirm-bill-to-modal';
import {
    IAccountShippingPreferences,
    IShippingPreference,
    PrecendencType,
} from '../../../../models/shipping-preferences';
import AutoSubmit from '../../../inputs/auto-submit';
import { FormikDebugger } from '../../../../helpers/formik-debugger';
import { useBusy } from '../../../../hooks/use-busy-hook';
import { toast } from 'react-toastify';
import Scroll from 'react-scroll';

interface FormValues {
    billTo: IAccountShippingPreferences | null;
    shipTo: IAccountShippingPreferences | null;
}

interface ShippingPreferencesProps {
    history: History;
}

const ShippingPreferences: React.FC<ShippingPreferencesProps> = () => {
    const intl = useIntl();
    const auth = useContext(authContext);
    const isReadOnly = !auth.buckets.find((x) => x === Buckets.Preferences);

    const [selectedBillTo, setSelectedBillTo] = useState('');
    const [selectedShipTo, setSelectedShipTo] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [tempBillToState, setTempBillToState] = useState<any>({});

    const [
        selectedBillToPrefs,
        reloadBillToPrefs,
        billToPrefErrors,
        saveDefaultPreferences,
    ] = useShippingPreferences(selectedBillTo, 'NULL', selectedBillTo === '');

    const [selectedShipToPrefs, reloadShipToPrefs, shipToPrefErrors] =
        useShippingPreferences(
            selectedBillTo,
            selectedShipTo,
            selectedShipTo === ''
        );

    const [setBusy] = useBusy();

    const handleSelectAccountsChange = (e: any) => {
        const { name, value } = e.target;
        if (name === 'billTo') {
            setSelectedBillTo(value);
        }

        if (name === 'shipTo') {
            setSelectedShipTo(value);
        }
    };

    const handleFormSubmit = async (
        values: FormValues,
        initialValues: FormValues
    ): Promise<boolean> => {
        const valuesToSave: IShippingPreference[] = [];

        for (let i = 0; i < values.billTo!.defaultShippingMethods.length; i++) {
            const updated = values.billTo!.defaultShippingMethods[i];
            const existing = initialValues.billTo!.defaultShippingMethods[i];

            if (updated.shippingMethod !== existing.shippingMethod) {
                const pref: IShippingPreference = {
                    ...updated,
                    billTo: values.billTo!.billTo,
                    precedence: updated.shippingMethod
                        ? PrecendencType.BillTo
                        : PrecendencType.Unspecified,
                };
                valuesToSave.push(pref);
            }
        }

        if (values.shipTo) {
            for (
                let i = 0;
                i < values.shipTo!.defaultShippingMethods.length;
                i++
            ) {
                const updated = values.shipTo!.defaultShippingMethods[i];
                const existing =
                    initialValues.shipTo!.defaultShippingMethods[i];

                if (updated.shippingMethod !== existing.shippingMethod) {
                    updated.shipTo = values.shipTo!.shipTo;
                    const pref: IShippingPreference = {
                        ...updated,
                        billTo: values.billTo!.billTo,
                        shipTo: values.shipTo!.shipTo,
                        precedence: PrecendencType.ShipTo,
                    };
                    valuesToSave.push(pref);
                }
            }
        }

        const saveResult = await saveDefaultPreferences(valuesToSave[0]);

        if (saveResult) {
            // may have to await these to prevent studder.
            if (!valuesToSave[0].shipTo) {
                reloadBillToPrefs();
                reloadShipToPrefs();
            } else {
                reloadShipToPrefs();
            }
        }

        return saveResult;
    };

    const debugForm = false;

    return (
        <>
            <PageTitle pageTitleId="Shipping_Preferences" />
            <h1 className="underline">
                <FormattedMessage id="Shipping_Preferences" />
            </h1>
            <Formik
                initialValues={{
                    billTo: selectedBillToPrefs,
                    shipTo: selectedShipToPrefs,
                }}
                onSubmit={(values, { setSubmitting, resetForm }) => {
                    const original = {
                        billTo: selectedBillToPrefs,
                        shipTo: selectedShipToPrefs,
                    };

                    setBusy(true);

                    handleFormSubmit(values, original).then((r: boolean) => {
                        setSubmitting(false);
                        if (!r) {
                            resetForm();
                            const scroll = Scroll.animateScroll;
                            scroll.scrollToBottom();
                        } else {
                            toast.success(
                                intl.formatMessage({ id: 'Changes_Saved' }),
                                { position: 'bottom-right', autoClose: 5000 }
                            );
                        }
                        setBusy(false);
                    });
                }}
                enableReinitialize={true}
            >
                {(props) => {
                    const {
                        values,
                        handleChange,
                        handleSubmit,
                        setFieldValue,
                        dirty,
                        submitForm,
                    } = props;

                    const handleBillToChange = (
                        e: React.ChangeEvent<any>,
                        index?: number
                    ) => {
                        const currentValue =
                            values.billTo!['defaultShippingMethods'][index!][
                                'shippingMethod'
                            ];

                        if (!currentValue) {
                            const { name } = e.target;

                            setTempBillToState({
                                name: name,
                                value: currentValue,
                            });

                            setShowConfirmModal(true);
                        }

                        handleChange(e);
                    };

                    const handleModalClose = () => {
                        setFieldValue(
                            tempBillToState.name,
                            tempBillToState.value
                        );
                        setTempBillToState({});
                        setShowConfirmModal(false);
                    };

                    const handleModalAccept = () => {
                        setShowConfirmModal(false);
                    };

                    const errorMessage = billToPrefErrors || shipToPrefErrors;
                    return (
                        <>
                            <form onSubmit={handleSubmit}>
                                <SelectAccounts
                                    onChange={handleSelectAccountsChange}
                                />

                                {values.billTo && (
                                    <div className="gutter-top">
                                        <ul className="nav nav-tabs">
                                            <li
                                                className={
                                                    !values.shipTo
                                                        ? ' active'
                                                        : ''
                                                }
                                            >
                                                <a
                                                    data-toggle="tab"
                                                    href="#billToPrefs"
                                                >
                                                    Bill To: {selectedBillTo}
                                                </a>
                                            </li>
                                            {values.shipTo && (
                                                <li className="active">
                                                    <a
                                                        data-toggle="tab"
                                                        href="#shipToPrefs"
                                                    >
                                                        Ship To:{' '}
                                                        {selectedShipTo}
                                                    </a>
                                                </li>
                                            )}
                                        </ul>
                                        <>
                                            <div className="tab-content">
                                                <div
                                                    id="billToPrefs"
                                                    className={
                                                        'fade in tab-pane' +
                                                        (!values.shipTo
                                                            ? ' active'
                                                            : '')
                                                    }
                                                >
                                                    <ShippingPreferencesTabPane
                                                        accountShippingPreferences={
                                                            values.billTo
                                                        }
                                                        accountType={
                                                            AccountType.BillTo
                                                        }
                                                        isReadOnly={
                                                            isReadOnly ||
                                                            values.billTo!
                                                                .buyingGroup
                                                        }
                                                        handleChange={
                                                            handleBillToChange
                                                        }
                                                    />
                                                </div>
                                                {values.shipTo && (
                                                    <div
                                                        id="shipToPrefs"
                                                        className="active fade in tab-pane"
                                                    >
                                                        <ShippingPreferencesTabPane
                                                            accountShippingPreferences={
                                                                values.shipTo
                                                            }
                                                            accountType={
                                                                AccountType.ShipTo
                                                            }
                                                            isReadOnly={
                                                                isReadOnly ||
                                                                values.shipTo!
                                                                    .buyingGroup
                                                            }
                                                            handleChange={
                                                                handleChange
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <FormActions
                                                cancelUrl="/my-account/preferences"
                                                hideSave={true}
                                                pullRight={true}
                                                cancelText={intl.formatMessage({
                                                    id: 'Go_Back',
                                                })}
                                            />
                                            {errorMessage && (
                                                <div className="alert alert-danger gutter-top">
                                                    <span
                                                        style={{
                                                            whiteSpace:
                                                                'pre-wrap',
                                                        }}
                                                    >
                                                        {parse(errorMessage)}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    </div>
                                )}
                            </form>
                            {debugForm && <FormikDebugger {...props} />}
                            {showConfirmModal && (
                                <ConfirmBilToModal
                                    onAccept={handleModalAccept}
                                    onClose={handleModalClose}
                                />
                            )}

                            {!showConfirmModal && (
                                <AutoSubmit
                                    dirty={dirty}
                                    submitForm={submitForm}
                                />
                            )}
                        </>
                    );
                }}
            </Formik>
        </>
    );
};

export default ShippingPreferences;
